import { Controller } from "@hotwired/stimulus";

// Copy protection for the modern book reader — the same policy the legacy
// book_reader enforces with jQuery: no right-click menu and no copying, except
// inside embedded answers (students type there) and when the URL hash is
// #showVideoId (an instructor tool). The layout leaves the controller off for
// accessibility-mode users. See docs/book-reader-modern.md.
//
// Copyable content: a selection that lies entirely inside a COPYABLE element is
// let through. `.excel-formula` is the chip the finance / financial-modeling /
// appraisal themes wrap an Excel formula in — the point of those books is that
// the student types the formula into a sheet, so copying it is the lesson, not
// a leak (Jess, 2026-10-19). `.copy-allowed` / `data-copy-allowed` are the
// theme-neutral opt-ins for any other content an author wants copyable.
const COPYABLE = ".excel-formula, .copy-allowed, [data-copy-allowed]";
const EDITABLE = ".embeded-answers, .answers, input, textarea, [contenteditable]";

export default class extends Controller {
  connect() {
    this.onCopy = this.onCopy.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);
    document.addEventListener("copy", this.onCopy);
    document.addEventListener("contextmenu", this.onContextMenu);
  }

  disconnect() {
    document.removeEventListener("copy", this.onCopy);
    document.removeEventListener("contextmenu", this.onContextMenu);
  }

  onCopy(event) {
    if (this.allowed(event)) return;
    event.preventDefault();
    alert("Please don't copy text. It is against our policies.");
  }

  onContextMenu(event) {
    if (this.allowed(event)) return;
    // On Android a long-press to select text fires `contextmenu`; blocking it
    // (and its alert) would make highlighting impossible on phones. Copying
    // itself is still blocked by onCopy. See docs/highlights.md.
    if (event.pointerType === "touch" || event.pointerType === "pen") return;
    event.preventDefault();
    alert("Please don't copy text. It is against our policies.");
  }

  allowed(event) {
    if (window.location.hash === "#showVideoId") return true;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (target?.closest(EDITABLE)) return true;
    if (target?.closest(COPYABLE)) return true;
    return this.selectionIsCopyable();
  }

  // The `copy` event's target is where the selection was anchored, so a drag that
  // starts just outside a chip and ends inside it (or the reverse) targets the
  // paragraph. Allow it anyway when both ends of the selection sit inside the
  // same copyable element — nothing outside it is being copied. A collapsed
  // selection (nothing to copy) still falls through to the block.
  selectionIsCopyable() {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;
    const owner = (node) => (node instanceof Element ? node : node?.parentElement)?.closest(COPYABLE);
    const anchor = owner(selection.anchorNode);
    return !!anchor && anchor === owner(selection.focusNode);
  }
}
